if (!customElements.get('search-dropdown')) {
  const escapeHtml = str =>
    String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')

  class SearchDropdown extends window.BAOv2.CustomElement {
    constructor () {
      super()
      this.eventBus = window.theme.autocomplete.eventBus
      this.events = window.theme.autocomplete.events
      this.config = window.theme.autocomplete.config

      this.lastResponse = null

      this.debouncedHandleInput = window.BAOv2.utils.debounce(
        this.handleInput.bind(this),
        this.config.debounceMs,
      )
    }

    // Dialog functionality - delegate to child dialog element
    get dialog () {
      return this.els.dialog?.element
    }

    get open () {
      return this.dialog?.open || false
    }

    showModal () {
      this.dialog?.showModal()
    }

    close () {
      this.dialog?.close()
    }

    connectedCallback () {
      super.connectedCallback()
      this.subscribeToEvents()

      const snapshot = window.theme.autocomplete.stateLayer?.state
      if (snapshot) this.onResponseUpdated(snapshot)
    }

    setupListeners () {
      super.setupListeners()

      this.externalInput = this.externalEls.input?.element

      document.querySelectorAll('[data-search-dropdown-external-el="input"]').forEach(input => {
        input.addEventListener('input', this.debouncedHandleInput.bind(this))
      })

      this.listeners.add(this.els.close.element, 'click', this.handleClose.bind(this))
      this.listeners.add(this.dialog, 'click', event => {
        if (event.target === this.dialog) this.handleClose(event)
      })
      this.listeners.add(document, 'keydown', event => {
        if (event.key === 'Escape' && this.open) {
          this.handleClose(event)
        }
      })

      const onKeywordClick = this.handleKeywordClick.bind(this)
      this.listeners.add(this.els.resultsSuggestions.element, 'click', onKeywordClick)
      this.listeners.add(this.els.initialSuggestions.element, 'click', onKeywordClick)

      this.listeners.add(
        this.els.resultsProducts.element,
        'click',
        this.handleProductCardClick.bind(this),
      )
    }

    subscribeToEvents () {
      this.eventBus.subscribe(this.events.STATE.RESPONSE_UPDATED, this.onResponseUpdated.bind(this))

      // Listen for drawer events to close search dropdown
      this.listeners.add(document, 'drawer:opened', this.handleClose.bind(this))

      this.listeners.add(document, 'drawer:closed', this.handleClose.bind(this))
    }

    onResponseUpdated (response) {
      this.lastResponse = response
      const view = this.pickView(response)

      this.setView(view)

      if (view === 'initial') {
        // this.renderRecentSearches(response.recentSearches)
        this.renderSuggestions(this.els.initialSuggestions.element, response.keywords, {
          skipIfPresent: true,
        })
      } else if (view === 'results') {
        this.renderProducts(response.products, response.activeTerm)
        this.renderSuggestions(this.els.resultsSuggestions.element, response.keywords)
        this.renderCategories(response.categories)
      } else if (view === 'no-results') {
        this.renderNoResults(response.activeTerm)
      }
    }

    pickView (response) {
      if (response.fetching) return 'initial'

      const hasTerm = response.activeTerm?.length > 1
      const hasHits = response.products?.hits?.length > 0

      if (hasTerm && hasHits) return 'results'
      if (hasTerm && !hasHits) return 'no-results'
      return 'initial'
    }

    setView (view) {
      const views = {
        initial: this.els.initialView.element,
        results: this.els.resultsView.element,
        'no-results': this.els.noResultsView.element,
      }

      Object.entries(views).forEach(([name, element]) => {
        if (name === view) {
          element.removeAttribute('hidden')
        } else {
          element.setAttribute('hidden', '')
        }
      })
    }

    renderProducts (products, activeTerm) {
      const list = this.els.resultsProducts.element.querySelector('ul')
      list.innerHTML = ''

      this.els.resultsProductsViewAll.element.setAttribute(
        'href',
        `/search?q=${encodeURIComponent(activeTerm)}`,
      )
      this.els.resultsProductsHeading.element.textContent = `Product results (${products.total})`

      products.hits.forEach((product, index) => {
        const listItem = document.createElement('li')
        listItem.innerHTML = window.theme.autocomplete.templates.ProductCard({
          product,
          index,
          type: 'autocomplete',
        })
        list.appendChild(listItem)
      })

      window.dispatchEvent(
        new CustomEvent('bao:modules:reinit', {
          detail: {
            root: list,
          },
        }),
      )

      this.querySelectorAll('.prd-Card').forEach(card => {
        card
          .querySelector('form')
          .addEventListener('submit', this.handleProductCardSubmit.bind(this))
      })
    }

    renderSuggestions (targetEl, keywords, { skipIfPresent = false } = {}) {
      if (skipIfPresent && targetEl.querySelectorAll('.hd-SearchDropdown_SuggestionsLink').length) {
        return
      }

      if (!keywords?.hits?.length) {
        targetEl.innerHTML = ''
        return
      }

      targetEl.innerHTML = this.templateSearchSuggestions(keywords.hits)
    }

    renderRecentSearches (recentSearches) {
      if (!recentSearches?.length) {
        this.els.initialRecentSearches.element.innerHTML = ''
        return
      }

      this.els.initialRecentSearches.element.innerHTML = this.templateRecentSearches(recentSearches)
      this.els.initialRecentSearches.element.removeAttribute('hidden')
    }

    renderCategories (categories) {
      if (!categories?.hits?.length) {
        this.els.resultsCategories.element.innerHTML = ''
        return
      }
      this.els.resultsCategories.element.innerHTML = this.templateSearchCategories(categories.hits)
    }

    renderNoResults (activeTerm) {
      this.els.noResultsHeading.element.textContent = `Sorry! No results for ${activeTerm}`
    }

    handleInput (event) {
      const query = event.target.value.trim()

      this.eventBus.publish(this.events.UI.QUERY_UPDATED, { query })
    }

    handleProductCardSubmit (event) {
      this.eventBus.publish(this.events.TRACKING.PRODUCT_ADDED_TO_CART, {
        productId: event.target.closest('.prd-Card').getAttribute('data-product-id'),
        productUrl: event.target.closest('.prd-Card').getAttribute('data-product-url'),
      })
    }

    handleClose () {
      if (!this.open) return

      document.querySelector('[data-search-dropdown-external-el="input"]').value = ''
      this.eventBus.publish(this.events.UI.QUERY_UPDATED, { query: '' })
      this.close()
      document.body.style.overflow = ''

      const customEvent = new CustomEvent('headerSearch::closed', {
        bubbles: true,
      })
      window.dispatchEvent(customEvent)
    }

    handleKeywordClick (event) {
      if (!event.target.closest('.hd-SearchDropdown_SuggestionsLink')) return

      const response = this.lastResponse
      if (!response) return

      this.eventBus.publish(this.events.TRACKING.KEYWORD_CLICKED, {
        request: { query: response.activeTerm },
        results: response.products,
      })
    }

    handleProductCardClick (event) {
      const productCard = event.target.closest('.prd-Card')
      const target = event.target?.href || false
      if (!productCard || !target) return

      this.eventBus.publish(this.events.TRACKING.PRODUCT_CLICKED, {
        productId: productCard.getAttribute('data-product-id'),
        productUrl: productCard.getAttribute('data-product-url'),
      })
    }

    templateSearchSuggestions (suggestions) {
      return `
        <div class="hd-SearchDropdown_SuggestionsHeader">
            <div class="hd-SearchDropdown_SuggestionsHeading">
              Search suggestions
            </div>
          </div>

          <ul class="hd-SearchDropdown_SuggestionsList">
            ${suggestions
              .map(
                suggestion => `
              <li class="hd-SearchDropdown_SuggestionsItem">
                <a href="/search?q=${encodeURIComponent(
                  suggestion.keyword,
                )}" class="hd-SearchDropdown_SuggestionsLink">
                  ${escapeHtml(suggestion.keyword)}
                </a>
              </li>
            `,
              )
              .join('')}
          </ul>
        `
    }

    templateRecentSearches (recentSearches) {
      return `
        <div class="hd-SearchDropdown_SuggestionsHeader">
            <div class="hd-SearchDropdown_SuggestionsHeading">
              Recent searches
            </div>
          </div>

          <ul class="hd-SearchDropdown_SuggestionsList">
            ${recentSearches
              .map(
                recent => `
              <li class="hd-SearchDropdown_SuggestionsItem">
                <a href="/search?q=${encodeURIComponent(
                  recent,
                )}" class="hd-SearchDropdown_SuggestionsLink">
                  ${escapeHtml(recent)}
                </a>
              </li>
            `,
              )
              .join('')}
          </ul>
        `
    }

    templateSearchCategories (categories) {
      return `
        <div class="hd-SearchDropdown_SuggestionsHeader">
            <div class="hd-SearchDropdown_SuggestionsHeading">
              Collection results
            </div>
          </div>

          <ul class="hd-SearchDropdown_SuggestionsList hd-SearchDropdown_SuggestionsList-vertical">
            ${categories
              .map(
                category => `
              <li class="hd-SearchDropdown_SuggestionsItem">
                <a href="${escapeHtml(category.url)}" class="hd-SearchDropdown_SuggestionsLink">
                  ${escapeHtml(category.name)}

                  <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style="transform: rotate(-90deg);"><rect x="9.19238" width="1" height="7" transform="rotate(45 9.19238 0)" fill="currentColor"></rect><rect x="0.707031" width="7" height="1" transform="rotate(45 0.707031 0)" fill="currentColor"></rect></svg>
                </a>
              </li>
            `,
              )
              .join('')}
          </ul>
        `
    }
  }

  customElements.define('search-dropdown', SearchDropdown)
}
